import { DocumentNode } from 'graphql'
import { LocationPaths, OwnSensorV2Path } from 'location.types'
import { LazyComponentWithPreload, lazyPreload } from 'utils/lazyPreload'
import { isLibreSurveyAnswer } from './libreIndirectSync'
import { OwnSensorV2Data } from './utils/ownSensorV2Storage'

export interface OwnSensorV2FlowStep {
  path: LocationPaths | OwnSensorV2Path
  location: string
  canNavigateBack: boolean
  routeComponent: LazyComponentWithPreload<() => JSX.Element | null>
  noPageAnimation?: boolean
  preloadQueries?: DocumentNode[]
  backgroundColor?: 'members.background' | 'members.backgroundAccent'
  fullScreenLayout?: boolean
  isComplete?: (storage: OwnSensorV2Data) => boolean
}

const OwnSensorV2LoadingScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/LoadingScreen').then((module) => ({
    default: module.LoadingScreen
  }))
)

const OwnSensorV2ChooseSensorScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/ChooseSensor').then((module) => ({
    default: module.ChooseSensor
  }))
)

const OwnSensorV2LibreIndirectSyncConfirmationScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/LibreIndirectSyncConfirmation').then((module) => ({
    default: module.LibreIndirectSyncConfirmation
  }))
)

const OwnSensorV2WhatBringsYouScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/WhatBringsYou').then((module) => ({
    default: module.WhatBringsYou
  }))
)

const OwnSensorV2Quote1Screen = lazyPreload(() =>
  import('screens/OwnSensorV2/Quote1').then((module) => ({
    default: module.Quote1
  }))
)

const OwnSensorV2TrackedGlucoseScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/TrackedGlucose').then((module) => ({
    default: module.TrackedGlucose
  }))
)

const OwnSensorV2AppOverviewScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/AppOverview').then((module) => ({
    default: module.AppOverview
  }))
)

const OwnSensorV2HeightScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/Height').then((module) => ({
    default: module.Height
  }))
)

const OwnSensorV2WeightScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/Weight').then((module) => ({
    default: module.Weight
  }))
)

const OwnSensorV2WellnessSnapshotScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/WellnessSnapshot').then((module) => ({
    default: module.WellnessSnapshot
  }))
)

const OwnSensorV2GoalWeightScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/GoalWeight').then((module) => ({
    default: module.GoalWeight
  }))
)

const OwnSensorV2Quote2Screen = lazyPreload(() =>
  import('screens/OwnSensorV2/Quote2').then((module) => ({
    default: module.Quote2
  }))
)

const OwnSensorV2ChronicConditionsScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/ChronicConditions').then((module) => ({
    default: module.ChronicConditions
  }))
)

const OwnSensorV2FamilyDiabetesScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/FamilyDiabetes').then((module) => ({
    default: module.FamilyDiabetes
  }))
)

const OwnSensorV2Quote3Screen = lazyPreload(() =>
  import('screens/OwnSensorV2/Quote3').then((module) => ({
    default: module.Quote3
  }))
)

const OwnSensorV2PreparingYourProgramScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/PreparingProgram').then((module) => ({
    default: module.PreparingYourProgram
  }))
)

const OwnSensorV2PlanSelectionScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/PlanSelection').then((module) => ({
    default: module.PlanSelection
  }))
)

const OwnSensorV2CreateAccountScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/CreateAccount').then((module) => ({
    default: module.CreateAccount
  }))
)

const OwnSensorV2CheckoutScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/Checkout').then((module) => ({
    default: module.Checkout
  }))
)

const OwnSensorV2SetPasswordScreen = lazyPreload(() =>
  import('screens/OwnSensorV2/SetPassword').then((module) => ({
    default: module.SetPassword
  }))
)

export const OwnSensorV2Flow: OwnSensorV2FlowStep[] = [
  {
    path: OwnSensorV2Path.LoadingScreen,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.LoadingScreen}`,
    canNavigateBack: false,
    routeComponent: OwnSensorV2LoadingScreen,
    backgroundColor: 'members.background',
    isComplete: (storage) => !!storage.selectedSensor
  },
  {
    path: OwnSensorV2Path.ChooseSensor,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.ChooseSensor}`,
    canNavigateBack: false,
    routeComponent: OwnSensorV2ChooseSensorScreen,
    isComplete: (storage) => !!storage.selectedSensor
  },
  {
    path: OwnSensorV2Path.LibreIndirectSyncConfirmation,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.LibreIndirectSyncConfirmation}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2LibreIndirectSyncConfirmationScreen,
    isComplete: (storage) => {
      if (!storage.libreIndirectSyncRequired) {
        return true
      }

      if (!isLibreSurveyAnswer(storage.selectedSensor)) {
        return true
      }

      return !!storage.libreIndirectSyncAcknowledged
    }
  },
  {
    path: OwnSensorV2Path.WhatBringsYou,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.WhatBringsYou}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2WhatBringsYouScreen,
    isComplete: (storage) => !!storage.goal
  },
  {
    path: OwnSensorV2Path.Quote1,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.Quote1}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2Quote1Screen,
    backgroundColor: 'members.backgroundAccent'
  },
  {
    path: OwnSensorV2Path.TrackedGlucose,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.TrackedGlucose}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2TrackedGlucoseScreen,
    isComplete: (storage) => !!storage.trackedGlucoseBefore
  },
  {
    path: OwnSensorV2Path.AppOverview,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.AppOverview}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2AppOverviewScreen,
    backgroundColor: 'members.backgroundAccent'
  },
  {
    path: OwnSensorV2Path.Height,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.Height}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2HeightScreen,
    isComplete: (storage) => storage.heightFeet != null && storage.heightInches != null
  },
  {
    path: OwnSensorV2Path.Weight,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.Weight}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2WeightScreen,
    isComplete: (storage) => !!storage.weight
  },
  {
    path: OwnSensorV2Path.WellnessSnapshot,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.WellnessSnapshot}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2WellnessSnapshotScreen
  },
  {
    path: OwnSensorV2Path.GoalWeight,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.GoalWeight}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2GoalWeightScreen,
    isComplete: (storage) => !!storage.goalWeight
  },
  {
    path: OwnSensorV2Path.Quote2,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.Quote2}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2Quote2Screen,
    backgroundColor: 'members.backgroundAccent'
  },
  {
    path: OwnSensorV2Path.ChronicConditions,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.ChronicConditions}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2ChronicConditionsScreen,
    isComplete: (storage) => !!storage.chronicConditions
  },
  {
    path: OwnSensorV2Path.FamilyDiabetes,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.FamilyDiabetes}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2FamilyDiabetesScreen,
    isComplete: (storage) => !!storage.familyDiabetes
  },
  {
    path: OwnSensorV2Path.Quote3,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.Quote3}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2Quote3Screen,
    backgroundColor: 'members.backgroundAccent'
  },
  {
    path: OwnSensorV2Path.PreparingYourProgram,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.PreparingYourProgram}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2PreparingYourProgramScreen
  },
  {
    path: OwnSensorV2Path.CreateAccount,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.CreateAccount}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2CreateAccountScreen,
    isComplete: () => false
  },
  {
    path: OwnSensorV2Path.PlanSelection,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.PlanSelection}`,
    canNavigateBack: false,
    routeComponent: OwnSensorV2PlanSelectionScreen,
    fullScreenLayout: true,
    isComplete: (storage) => !!storage.selectedProduct
  },
  {
    path: OwnSensorV2Path.Checkout,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.Checkout}`,
    canNavigateBack: true,
    routeComponent: OwnSensorV2CheckoutScreen
  },
  {
    path: OwnSensorV2Path.SetPassword,
    location: `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.SetPassword}`,
    canNavigateBack: false,
    routeComponent: OwnSensorV2SetPasswordScreen
  }
]
